import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useState } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { Order } from '../../models/InvoicesOrders';
import { SelectionToolbar } from '../interaction';
import { useInvoicesPermissions } from '../../hooks/useInvoicesPermissions';

interface InvoicesOrdersAgGridTableProps {
  orders: Order[];
  className?: string;
  selectedOrders?: Set<number>;
  onClearSelection?: () => void;
  selectedColumns?: string[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSelectionChanged?: (selectedOrders: Set<number>) => void;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  onEditSingleShipment?: (shipmentId: number) => void;
  onBlockSingleShipment?: (shipmentId: number) => void;
  onInvoiceSingleShipment?: (shipmentId: number) => void;
  onBulkInvoiceShipments?: () => void;
  onBulkEditShipments?: () => void;
}

export const InvoicesOrdersAgGridTable = React.memo(function InvoicesOrdersAgGridTable({
  orders,
  selectedOrders,
  onClearSelection,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onSelectionChanged,
  sorting,
  onSortingChange,
  onEditSingleShipment,
  onBlockSingleShipment,
  onInvoiceSingleShipment,
  onBulkInvoiceShipments,
  onBulkEditShipments,
}: InvoicesOrdersAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);
  const [localSelectedCount, setLocalSelectedCount] = useState(0);
  const { canCreatePreinvoices } = useInvoicesPermissions();
  const selectionEnabled = canCreatePreinvoices && Boolean(onBulkInvoiceShipments);

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  const { filteredColumnDefs } = useAgGridColumns({
    hasTeamData,
    selectedColumns,
    onEditSingleShipment,
    onBlockSingleShipment,
    onInvoiceSingleShipment,
  });

  const handleSelectionChanged = useCallback(
    (selectedRows: Order[]) => {
      if (!selectionEnabled) {
        return;
      }

      console.log('🧪 [EligibleShipments] onSelectionChanged fired', {
        selectionEnabled,
        selectedRowsCount: selectedRows.length,
        selectedRowsSample: selectedRows.slice(0, 5)?.map((row) => row.id),
      });

      const selectedIds = new Set(selectedRows.map((order) => order.id));
      setLocalSelectedCount(selectedRows.length);

      if (onSelectionChanged) {
        onSelectionChanged(selectedIds);
      }
    },
    [onSelectionChanged, selectionEnabled],
  );

  React.useEffect(() => {
    if (!selectionEnabled) {
      if (localSelectedCount !== 0) {
        console.log('🧪 [EligibleShipments] selection disabled, clearing all selections');
        clearSelectionUI();
        onClearSelection?.();
      }
      return;
    }

    if (selectedOrders && selectedOrders.size === 0 && localSelectedCount !== 0) {
      console.log('🧪 [EligibleShipments] external selection set cleared, syncing grid deselect');
      clearSelectionUI();
    }
  }, [selectedOrders, clearSelectionUI, selectionEnabled, onClearSelection, localSelectedCount]);

  return (
    <div className="w-full">
      <AgGridTable
        ref={agGridRef}
        data={orders}
        columnDefs={filteredColumnDefs as ColDef[]}
        height="calc(100vh - 200px)"
        enableSelection={selectionEnabled}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={() => undefined}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}}
        sorting={sorting}
        onSortingChange={onSortingChange}
        rowHeight={50}
      />

      {selectionEnabled && (
        <SelectionToolbar
          selectedCount={localSelectedCount || selectedOrders?.size || 0}
          onClose={handleCloseSelectionToolbar}
          onPrefacturar={onBulkInvoiceShipments}
          onBulkEdit={onBulkEditShipments}
        />
      )}
    </div>
  );
});
